import { SmallButton } from "@sellar-io/components";
import { useState } from "react";
import { twMerge } from "tailwind-merge";
import { useMediaQuery } from "usehooks-ts";
import { useShallow } from "zustand/react/shallow";
import { Icon } from "../../../components";
import { NavigationBarMenu } from "../../../components/navigation/NavigationBar";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import {
  StockOverviewSortType,
  StockOverviewSortValues,
} from "../StockOverviewFilterTypes";
import { useStockOverviewStore } from "../StockOverviewStore";
import { StockOverviewTable } from "../components/StockOverviewTable";
import { useStockOverview } from "../../../hooks/api/v2/stockOverview/useStockOverview";
import { StockOverviewCardList } from "../components/StockOverviewCardList";
import {
  STOCK_OVERVIEW_PRODUCT_DETAILS,
  NEW_PRODUCT,
} from "../../../constants/routes";
import { useNavigate } from "react-router";
import { useDebounce } from "use-debounce";
import { StockOverviewListResponseType } from "@sellar-io/api-v2/types";
import StockManagementDetailsDialog from "../../product/list/StockManagementDetailsDialog/StockManagementDetailsDialog";
import ItemDetailsDialog from "../../product/list/ProductItemModal/ItemDetailsDialog";
import { ProductDeleteDialog } from "../components/ProductDeleteDialog";
import { SearchAndFilterCard } from "../components/SearchAndFilterCard";
import { useNavigateToChild } from "../../../hooks/useParentNavigation";

export const StockOverview = () => {
  const { business } = useSellarCurrentBusiness();

  if (!business?.Supplier) throw new Error("Cannot find supplier");

  const isDesktop = useMediaQuery("(min-width: 1200px)");

  const [selectedProduct, setSelectedProduct] = useState<
    StockOverviewListResponseType["data"][number] | undefined
  >();
  const [stockManagementProduct, setStockManagementProduct] =
    useState<StockOverviewListResponseType["data"][number]>();
  const [showProductDeleteDialog, setShowProductDeleteDialog] = useState(false);
  const [
    showStockManagementDetailsDialog,
    setShowStockManagementDetailsDialog,
  ] = useState(false);
  const [showProductDetailsDialog, setShowProductDetailsDialog] =
    useState(false);

  const navigate = useNavigate();
  const navigateToChild = useNavigateToChild();

  const state = useStockOverviewStore(useShallow((state) => state));
  const [debouncedSearchValue] = useDebounce(state.search, 500);

  const handleTableSort = (columnSort: StockOverviewSortType | null) => {
    state.setColumnSort(columnSort ?? "parent_name_asc");
  };

  const handleNewProductClick = () => {
    navigate(NEW_PRODUCT);
  };

  const stockOverviewQueryParams = [
    ...(state.containerType
      ? ([
          {
            type: "containerType",
            modifier: "EQUALS",
            value: state.containerType,
          },
        ] as const)
      : []),

    ...(state.productStatus
      ? ([
          {
            type: "productStatus",
            modifier: "EQUALS",
            value: state.productStatus,
          },
        ] as const)
      : []),

    ...(state.itemStatus
      ? ([
          {
            type: "itemStatus",
            modifier: "EQUALS",
            value: state.itemStatus,
          },
        ] as const)
      : []),

    ...(state.inStock
      ? ([
          {
            type: "inStock",
            modifier: "EQUALS",
            value: state.inStock,
          },
        ] as const)
      : []),

    ...(state.preOrder
      ? ([
          {
            type: "preOrder",
            modifier: "EQUALS",
            value: state.preOrder,
          },
        ] as const)
      : []),
  ].flatMap((x) => (x ? x : []));

  const stockOverviewQuery = useStockOverview({
    limit: 50,
    search: debouncedSearchValue,
    query: stockOverviewQueryParams,
    sort: StockOverviewSortValues[state.columnSort]
      ? [StockOverviewSortValues[state.columnSort].mutation]
      : [],
  });

  const totalResultCount = stockOverviewQuery.data?.pages[0]?.meta.count ?? 0;

  return (
    <div className="h-screen">
      <NavigationBarMenu
        title="Stock Overview"
        borderBottom={false}
        rightComponent={
          <SmallButton onClick={handleNewProductClick}>
            <Icon icon={"PLUS"} size={0.7} /> New
          </SmallButton>
        }
      />

      {/* stock overview page */}

      <div
        className={twMerge(
          "container mx-auto mt-0 flex max-w-[1300px] flex-col bg-zinc-100",
          isDesktop ? "px-4" : "",
        )}
      >
        {/* search and filter */}

        <SearchAndFilterCard
          filterOptions={{
            itemStatus: state.itemStatus,
            productStatus: state.productStatus,
            containerType: state.containerType,
            inStock: state.inStock,
            preOrder: state.preOrder,
          }}
          setFilterOptions={(filterOptions) => {
            state.setItemStatus(filterOptions.itemStatus);
            state.setProductStatus(filterOptions.productStatus);
            state.setContainerType(filterOptions.containerType);
            state.setInStock(filterOptions.inStock);
            state.setPreOrder(filterOptions.preOrder);
          }}
          search={state.search}
          onSearchChange={(value) => {
            state.setSearch(value);
          }}
          columnSort={state.columnSort}
          onColumnSortChange={(sortType) => {
            state.setColumnSort(sortType);
          }}
          totalResultCount={totalResultCount}
        />

        {/* mobile/desktop product list/table view  */}

        {isDesktop ? (
          <StockOverviewTable
            stockOverviewQuery={stockOverviewQuery}
            handleTableSort={handleTableSort}
            onShowProductStockManagementDialog={(product) => {
              setStockManagementProduct(product);
              setShowStockManagementDetailsDialog(true);
            }}
            onShowProductDetailsDialog={(product) => {
              setSelectedProduct(product);
              setShowProductDetailsDialog(true);
            }}
            onShowProductDeleteDialog={(product) => {
              setSelectedProduct(product);
              setShowProductDeleteDialog(true);
            }}
          />
        ) : (
          <StockOverviewCardList
            stockOverviewQuery={stockOverviewQuery}
            onClickProduct={(productId) =>
              navigateToChild(
                STOCK_OVERVIEW_PRODUCT_DETAILS.href({ productId }),
              )
            }
          />
        )}
      </div>

      {/* delete product dialog */}

      <ProductDeleteDialog
        productName={selectedProduct?.name?.split("•")[1] ?? "product"}
        productId={selectedProduct?.id}
        onDismiss={() => {
          setShowProductDeleteDialog(false);
        }}
        active={showProductDeleteDialog}
      />

      {/* edit stock management dialog */}

      <StockManagementDetailsDialog
        active={showStockManagementDetailsDialog}
        onDismiss={() => setShowStockManagementDetailsDialog(false)}
        afterLeave={() => setStockManagementProduct(undefined)}
        product={stockManagementProduct}
      />

      {/* edit item details dialog */}

      <ItemDetailsDialog
        active={showProductDetailsDialog}
        onDismiss={() => setShowProductDetailsDialog(false)}
        product={selectedProduct}
      />
    </div>
  );
};
